import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { StoreContext } from '../contexts/StoreContext';
import { navItems } from '../constants/navItems';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS } from '../constants/layout';

function NavigationBar() {
  const location = useLocation();
  const { store, loading } = useContext(StoreContext);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Helper to see if a given path is "active"
  const isActive = (path) => {
    if (path === '/') {
      return (
        location.pathname === '/' ||
        location.pathname.startsWith('/product/')
      );
    }
    return location.pathname === path;
  };

  const linkStyle = (path) => ({
    textDecoration: 'none',
    color: isActive(path) ? '#ffffff' : '#000000',
    fontWeight: isActive(path) ? '700' : 'normal',
    padding: '6px 12px',
    borderRadius: BORDER_RADIUS,
    backgroundColor: isActive(path) ? '#000000' : 'transparent',
    transition: 'all 0.2s ease',
    display: 'block',
  });

  // Only show the store name once it's loaded and available
  const storeTitle = !loading && store?.name ? store.name : '';

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        <nav
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid #e5e5e5',
          }}
        >
          <div
            className="nav-safe-area"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
            }}
          >
            <Link
              to="/"
              style={{
                textDecoration: 'none',
                color: 'black',
                fontSize: '20px',
                letterSpacing: '-0.02em',
                fontFamily: "'Instrument Serif', serif",
                fontWeight: '700',
              }}
            >
              {storeTitle}
            </Link>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                width: '24px',
                height: '24px',
                position: 'relative',
              }}
              aria-label="Toggle menu"
            >
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: '#374151',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(45deg) translate(5px, 5px)'
                    : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: '#374151',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: '#374151',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(-45deg) translate(7px, -6px)'
                    : 'none',
                }}
              />
            </button>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99,
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu Panel (translucent background) */}
        <div
          className="sidebar-safe-area-right"
          style={{
            position: 'fixed',
            top: 0,
            right: isMenuOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
            zIndex: 101,
            transition: 'right 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '80px', // Space for the nav bar
          }}
        >
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '0' }}>
            {navItems.map((item, i) => (
              <React.Fragment key={item.path}>
                <Link
                  to={item.path}
                  className="sidebar-link"
                  style={{
                    textDecoration: 'none',
                    color: isActive(item.path) ? '#ffffff' : '#000000',
                    fontWeight: isActive(item.path) ? '600' : 'normal',
                    padding: '10px 12px',
                    borderRadius: 0,
                    backgroundColor: isActive(item.path) ? '#000000' : 'transparent',
                    transition: 'all 0.2s ease',
                    display: 'block',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.icon && (
                      <span className="material-icons" style={{ fontSize: '20px', width: '24px', textAlign: 'center', flexShrink: 0 }}>
                        {item.icon}
                      </span>
                    )}
                    {item.label}
                  </span>
                </Link>
                {i < navItems.length - 1 && (
                  <div style={{ margin: '0 12px', borderBottom: '1px solid #e5e5e5' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .sidebar-link:hover {
            background-color: rgba(0, 0, 0, 0.06) !important;
          }
        `}</style>
      </>
    );
  }

  // Desktop Navigation (links grouped on the right)
  return (
    <nav
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 100,
        borderBottom: '1px solid #e5e5e5',
        paddingTop: '12px',
        paddingBottom: '12px',
      }}
    >
      <div
        className="nav-safe-area"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '20px',
          paddingRight: '20px',
          maxWidth: MAX_CONTENT_WIDTH,
          margin: '0 auto',
        }}
      >
        <Link
          to="/"
          style={{
            textDecoration: 'none',
            color: 'black',
            fontSize: '20px',
                letterSpacing: '-0.02em',
            fontFamily: "'Instrument Serif', serif",
            fontWeight: '700',
          }}
        >
          {storeTitle}
        </Link>

        <div style={{ display: 'flex', gap: '24px' }}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={linkStyle(item.path)}
              onMouseEnter={e => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }
              }}
              onMouseLeave={e => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}

export default NavigationBar;
